import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { Download, FileJson, FileSpreadsheet, CheckCircle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ExportPanel = () => {
  const [exporting, setExporting] = useState(null);
  const [exported, setExported] = useState(null);

  const handleExport = async (format) => {
    setExporting(format);
    setExported(null);
    
    try {
      const response = await fetch(`${API}/export/${format}`);
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `moodpunk_export.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setExported(format);
      setTimeout(() => setExported(null), 3000);
    } catch (error) {
      console.error("Error exporting:", error);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-8">
      <p className="text-slate-300 font-inter leading-relaxed">
        Exporta todos tus registros de estado de ánimo para respaldo o análisis externo.
        Tus datos incluyen la fecha, estado de ánimo, emoji, valor numérico y notas.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* CSV Export */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-black/30 rounded-xl p-6 border border-white/5 hover:border-neon-green/30 transition-colors"
        >
          <div className="flex items-start gap-4 mb-4">
            <div className="p-3 rounded-lg bg-neon-green/10 border border-neon-green/30">
              <FileSpreadsheet className="w-8 h-8 text-neon-green" />
            </div>
            <div>
              <h3 className="font-rajdhani font-bold text-lg text-white uppercase tracking-wide">
                Exportar CSV
              </h3>
              <p className="text-slate-400 text-sm mt-1">
                Compatible con Excel, Google Sheets y más
              </p>
            </div>
          </div>
          
          <ul className="text-sm text-slate-400 space-y-2 mb-6">
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-neon-green" />
              Formato tabular fácil de leer
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-neon-green" />
              Ideal para hojas de cálculo
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-neon-green" />
              Importación sencilla en otras apps
            </li>
          </ul>

          <Button
            onClick={() => handleExport("csv")}
            disabled={exporting === "csv"}
            data-testid="export-csv-btn"
            className="w-full export-btn font-rajdhani font-bold uppercase tracking-widest px-6 py-3 rounded-lg bg-neon-green/10 hover:bg-neon-green/20 text-neon-green border border-neon-green/50 hover:shadow-[0_0_20px_rgba(10,255,96,0.4)] transition-all duration-300"
          >
            {exporting === "csv" ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : exported === "csv" ? (
              <CheckCircle className="w-4 h-4 mr-2" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            {exported === "csv" ? "¡Descargado!" : "Descargar CSV"}
          </Button>
        </motion.div>

        {/* JSON Export */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-black/30 rounded-xl p-6 border border-white/5 hover:border-neon-purple/30 transition-colors"
        >
          <div className="flex items-start gap-4 mb-4">
            <div className="p-3 rounded-lg bg-neon-purple/10 border border-neon-purple/30">
              <FileJson className="w-8 h-8 text-neon-purple" />
            </div>
            <div>
              <h3 className="font-rajdhani font-bold text-lg text-white uppercase tracking-wide">
                Exportar JSON
              </h3>
              <p className="text-slate-400 text-sm mt-1">
                Formato estructurado para desarrolladores
              </p>
            </div>
          </div>
          
          <ul className="text-sm text-slate-400 space-y-2 mb-6">
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-neon-purple" />
              Estructura de datos completa
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-neon-purple" />
              Ideal para análisis con código
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-neon-purple" />
              Fácil de procesar con scripts
            </li>
          </ul>

          <Button
            onClick={() => handleExport("json")}
            disabled={exporting === "json"}
            data-testid="export-json-btn"
            className="w-full export-btn font-rajdhani font-bold uppercase tracking-widest px-6 py-3 rounded-lg bg-neon-purple/10 hover:bg-neon-purple/20 text-neon-purple border border-neon-purple/50 hover:shadow-[0_0_20px_rgba(188,19,254,0.4)] transition-all duration-300"
          >
            {exporting === "json" ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : exported === "json" ? (
              <CheckCircle className="w-4 h-4 mr-2" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            {exported === "json" ? "¡Descargado!" : "Descargar JSON"}
          </Button>
        </motion.div>
      </div>

      {/* Info Box */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-neon-cyan/5 border border-neon-cyan/20 rounded-xl p-4"
      >
        <p className="text-sm text-neon-cyan/80 font-inter">
          <strong>Nota:</strong> Tus datos son privados y solo se almacenan localmente. 
          La exportación incluye todos los registros desde el inicio de tu uso de la aplicación.
        </p>
      </motion.div>
    </div>
  );
};
